import * as React from 'react';

import { Check, Info, Loader, XCircle } from 'lucide-react';
import {
  composeRenderProps,
  TextArea as AriaTextArea,
  TextAreaProps as AriaTextAreaProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { IconButton } from '../buttons/IconButton';
import { RawTooltip, TooltipTrigger } from '../overlays/Tooltip';

export interface NotesLabels {
  saving?: string;
  saved?: string;
  failed?: string;
  noNotesAvailable?: string;
  clearAll?: string;
}

export interface NotesProps {
  title: string;
  subtitle: string;
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  readOnly?: boolean;
  loading?: boolean;
  success?: boolean;
  showStatusBadge?: boolean;
  className?: string;
  placeholder?: string;
  labels?: NotesLabels;
}

const TextArea = ({ className, ...props }: AriaTextAreaProps) => (
  <AriaTextArea
    className={composeRenderProps(className, (className) =>
      cn(
        '@TextArea',
        'flex min-h-[80px] w-full rounded-md border border-neutral/40',
        'px-3 py-2 text-sm ring-offset-neutral/10',
        'placeholder:text-neutral/20',
        'rac-focus:ring-2 rac-focus:ring-primary rac-focus:ring-offset-2 rac-focus:outline-none',
        'rac-disabled:cursor-not-allowed rac-disabled:opacity-50',
        'focus-visible:outline-none',
        className,
      ),
    )}
    {...props}
  />
);

export const Notes = ({
  title,
  subtitle,
  onClear,
  readOnly,
  loading,
  success,
  showStatusBadge = false,
  className,
  value,
  placeholder,
  onChange,
  labels,
}: NotesProps) => {
  const {
    saving = 'Saving...',
    saved = 'Saved',
    failed = 'Failed',
    noNotesAvailable = 'No notes available',
    clearAll = 'Clear All',
  } = labels ?? {};

  const [_showBadge, setShowBadge] = React.useState(false);

  React.useEffect(() => {
    if (loading) {
      setShowBadge(true);
      return;
    }

    if (success !== undefined) {
      setShowBadge(true);
      const timer = setTimeout(() => {
        setShowBadge(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [loading, success]);

  const handleChange = (value: string) => {
    if (!readOnly) {
      onChange(value);
    }
  };

  const badgeState = React.useMemo(() => {
    if (loading) {
      return {
        icon: <Loader className="animate-spin" size={14} />,
        text: saving,
      };
    }

    if (success) {
      return {
        icon: (
          <div className="flex size-4 items-center justify-center rounded-full bg-success">
            <Check size={12} className="text-white" />
          </div>
        ),
        text: saved,
      };
    }

    return {
      icon: <XCircle size={14} />,
      text: failed,
    };
  }, [loading, success, saving, saved, failed]);

  return (
    <div className={cn('@Notes', 'relative size-full', className)}>
      <TextArea
        placeholder={readOnly && !value ? noNotesAvailable : placeholder}
        readOnly={readOnly}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          handleChange(e.target.value)
        }
        className={cn(
          'size-full h-full resize-none rounded-lg bg-white px-5 pt-16 pb-4 text-base',
          'placeholder:text-neutral/40',
          readOnly
            ? 'cursor-not-allowed text-neutral/80 ring-0 outline-none focus:ring-0 focus:outline-none'
            : 'cursor-text border border-neutral/20 text-neutral focus:ring-2 focus:ring-primary',
        )}
      />
      <div className="absolute inset-x-5 top-4">
        <div className="flex justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold">{title}</span>
            <TooltipTrigger delay={12}>
              <IconButton
                variant="unstyled"
                className="size-[18px] text-neutral/60"
                icon={<Info className="text-neutral/60" size={18} />}
              />
              <RawTooltip placement="top" className="z-[1000]">
                <div className="px-2 py-1 text-sm">{subtitle}</div>
              </RawTooltip>
            </TooltipTrigger>
            <div
              className={cn(
                'flex h-7 flex-row items-center gap-1 rounded-[5px] px-2 py-1 transition duration-300',
                showStatusBadge ? 'opacity-100' : 'opacity-0',
                loading
                  ? 'bg-neutral/10 text-neutral'
                  : 'bg-failure/20 text-failure',
                success && 'bg-success/20 text-success',
              )}
            >
              {badgeState.icon}
              <span className="text-xs font-semibold capitalize">
                {badgeState.text}
              </span>
            </div>
          </div>
          <div
            className={cn(
              value && !readOnly ? 'flex' : 'hidden',
              'items-center gap-2 text-neutral/60 hover:cursor-pointer hover:text-primary',
            )}
            onClick={onClear}
          >
            <XCircle size={16} className="text-neutral/60" />
            <span className="text-lg leading-4 font-medium text-neutral/60">
              {clearAll}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
